<script lang="ts">
  import type {CinemaSponsoredProduct} from "../../types/CinemaProductsDto.types.ts";
  import DiscountTag from "./sales-tags/DiscountTag.svelte";
  import BenefitTag from "./sales-tags/BenefitTag.svelte";
  import ProductBadge from "./product-badge/ProductBadge.svelte";
  import type {TrackingLabels} from "../../lib/tracking/trackingUtils.ts";
  import {
    buildCinemaFeatureForClick,
    buildClickBaseLabels,
    buildTileFeatureForClick,
  } from "../../lib/tracking/trackingUtils.ts";
  import {getContext, onMount} from "svelte";
  import cinemaTilePromiseKey, {type AddPromise,} from "../../lib/tracking/cinemaTilePromiseKey";
  import CleanTitle from "./title/CleanTitle.svelte";
  import CleanBrand from "./brand/CleanBrand.svelte";
  import CleanOttoUp from "./otto-up/CleanOttoUp.svelte";
  import CleanEekLabel from "./energy-label/CleanEekLabel.svelte";
  import CleanOfcPriceComponent from "./price/CleanOfcPriceComponent.svelte";
  import CleanPriceSection from "./price/CleanPriceSection.svelte";
  import CleanProductImage from "./product-image/CleanProductImage.svelte";

  type CinemaProductProps = {
    variation: CinemaSponsoredProduct;
    lazyLoad: boolean;
    auctionId: string;
    position: number;
    cinemaTrackingLabels: TrackingLabels;
    useOfcPricingItem?: boolean;
    useOfcWishlistSaveButton?: boolean;
  };

  const addPromise = getContext<AddPromise | undefined>(cinemaTilePromiseKey);
  if (typeof addPromise === "function") {
    addPromise(
      new Promise<void>((resolve) => {
        onMount(() => resolve());
      }),
    );
  }

  const {
    variation,
    lazyLoad,
    auctionId,
    position,
    cinemaTrackingLabels,
    useOfcPricingItem,
    useOfcWishlistSaveButton,
  }: CinemaProductProps = $props();

  const clickFeatures = $derived([
    buildCinemaFeatureForClick(auctionId, cinemaTrackingLabels),
    buildTileFeatureForClick(
      auctionId,
      variation.variationId,
      position,
      variation.tileTrackingLabels as TrackingLabels,
    ),
  ]);

  const clickBaseLabels = $derived(buildClickBaseLabels(clickFeatures));

  const showOfcPricingItem = $derived(
    useOfcPricingItem && variation.priceForOfcPricingItem?.retailPrice != null,
  );
</script>

<div>
  <div class="tile-content">
    <oc-link-v2
      base64-href={btoa(variation.detailPageLink)}
      oc-aria-label={`${variation.brand} – ${variation.title}`}
    >
      <a
        class="detail-page-url"
        href={variation.canonicalLink}
        data-tr-v1={JSON.stringify(clickBaseLabels)}
        data-tr-v1.track="click,auxclick"
        data-tr-v1.method="submitMove"
        data-tr-v1.action={JSON.stringify({
          name: "click",
          features: clickFeatures,
        })}
      >
        <div class="image-container">
          <CleanProductImage image={variation.image} {lazyLoad}>
            <ProductBadge variationId={variation.variationId}/>
          </CleanProductImage>
        </div>
        <div>
          <CleanBrand>{variation.brand}</CleanBrand>
          <CleanTitle>{variation.title}</CleanTitle>
        </div>
        <div class="price-component">
          {#if showOfcPricingItem}
            <CleanOfcPriceComponent variationId={variation.variationId}
                               priceForOfcPricingItem={variation.priceForOfcPricingItem!}/>
          {:else}
            <CleanPriceSection price={variation.price}></CleanPriceSection>
          {/if}
        </div>
        {#if !useOfcPricingItem}
          <div class="tags">
            {#if variation.salesTags}
              <DiscountTag discount={variation.salesTags.discount}/>
            {/if}
            <BenefitTag variationId={variation.variationId}/>
          </div>
        {/if}
        {#if variation.ottoUp}
          <CleanOttoUp/>
        {/if}
      </a>
    </oc-link-v2>
    {#if useOfcWishlistSaveButton}
      <div class="wishlist-save-button">
        <ofc-wishlist-save-button-v1
          variant="icon-button-transparent"
          variation-id={variation.variationId}
          qualified={variation.multipleSizes ? "false" : "true"}
          source="recoSpa"
        ></ofc-wishlist-save-button-v1>
      </div>
    {/if}
    <div>
      {#if variation.energyLabels.length}
        <CleanEekLabel
          labels={variation.energyLabels}
        />
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .tile-content {
    background-color: tokens.$oc-base-color-white-100;
    overflow: hidden;
    position: relative;
    padding-bottom: tokens.$oc-semantic-spacing-75;
  }

  .image-container {
    overflow: hidden;
    border-radius: tokens.$oc-semantic-border-radius-150;
  }

  .price-component {
    margin-bottom: tokens.$oc-semantic-spacing-100;
  }

  .wishlist-save-button {
    position: absolute;
    right: 6px;
    top: 6px;
    z-index: 1;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: tokens.$oc-semantic-spacing-25;
    margin-top: tokens.$oc-semantic-spacing-25;
  }

  .detail-page-url {
    color: tokens.$oc-base-color-black-100;
  }

  .detail-page-url:hover {
    text-decoration: tokens.$oc-base-color-black-100 underline;
    /*                                                                                                                   */
    /*                                                                                                   */
    --tile-hover-decoration-none: #{tokens.$oc-base-color-gray-25} underline;
    --tile-hover-decoration-grey: #{tokens.$oc-semantic-color-text-secondary}
    underline;
  }
</style>
